import React, { useMemo, useState } from "react";
import { useApiData } from "@/hooks/useApiData";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import RefreshButton from "@/components/RefreshButton";
import { downloadFile, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ReceiptText, FileDown, AlertCircle, Loader2, ExternalLink, Eye, Repeat, Landmark, CheckCircle2, Clock } from "lucide-react";
import { formatDate, formatMoney } from "@/lib/format";
import { toast } from "sonner";

/**
 * Derive payment method + status from the invoice.
 * - gocardless_payment_id present  => Direct Debit collected via GoCardless
 * - otherwise                      => Bank transfer
 * status enum: draft | sent | paid | void
 */
function getPayment(inv) {
  const gc = !!inv.gocardless_payment_id;
  const st = (inv.status || "").toLowerCase();
  const method = gc ? "Direct Debit" : "Bank transfer";
  const MethodIcon = gc ? Repeat : Landmark;
  if (st === "paid") {
    return {
      method, MethodIcon,
      label: gc ? "Collected by GoCardless" : "Paid by bank transfer",
      icon: CheckCircle2,
      cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
    };
  }
  if (st === "void") {
    return { method: null, MethodIcon: null, label: "Cancelled", icon: null, cls: "bg-slate-100 text-slate-500 border-slate-200" };
  }
  if (st === "draft") {
    return { method, MethodIcon, label: "Not yet issued", icon: Clock, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  }
  // sent = issued, awaiting payment
  return gc
    ? { method, MethodIcon, label: "Due to be collected by GoCardless", icon: Clock, cls: "bg-sky-50 text-sky-800 border-sky-200" }
    : { method, MethodIcon, label: "Awaiting bank transfer", icon: Clock, cls: "bg-amber-50 text-amber-900 border-amber-200" };
}

const PaymentBadge = ({ inv, testId }) => {
  const p = getPayment(inv);
  const Icon = p.icon;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${p.cls}`}>
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {p.label}
    </span>
  );
};

const PaymentMethodLine = ({ inv }) => {
  const p = getPayment(inv);
  if (!p.method) return null;
  const M = p.MethodIcon;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      {M && <M className="h-3.5 w-3.5 text-[color:var(--hh-amber-600)]" />}
      {p.method}
    </span>
  );
};

export default function Invoices() {
  const { data, loading, refreshing, error, refetch } = useApiData("/portal/invoices");
  const [downloading, setDownloading] = useState("");
  const [selected, setSelected] = useState(null);

  const onRefresh = async () => {
    const ok = await refetch();
    if (ok) toast.success("Invoices up to date");
  };

  const invoices = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    return [...arr].sort((a, b) => new Date(b.period_end || b.created_at || 0) - new Date(a.period_end || a.created_at || 0));
  }, [data]);

  const outstanding = useMemo(() => {
    return invoices
      .filter((i) => !["paid", "void"].includes((i.status || "").toLowerCase()))
      .reduce((sum, i) => sum + (Number(i.total) || 0), 0);
  }, [invoices]);

  const period = (inv) => {
    if (inv.period_start && inv.period_end) return `${formatDate(inv.period_start)} \u2013 ${formatDate(inv.period_end)}`;
    return formatDate(inv.created_at);
  };

  const handleDownload = async (inv) => {
    setDownloading(inv.id);
    try {
      const isXero = !!inv.xero_invoice_id;
      const url = isXero ? `/api/invoices/${inv.id}/xero-pdf` : `/api/invoices/${inv.id}/pdf`;
      await downloadFile({ upstreamUrl: url, filename: `Invoice-${inv.xero_invoice_number || inv.id}.pdf` });
      toast.success("Invoice downloaded");
    } catch (e) {
      toast.error(extractError(e, "Download failed"));
    } finally {
      setDownloading("");
    }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Invoices" subtitle="Your invoices, Xero status and Direct Debit / bank transfer payments" actions={<RefreshButton refreshing={refreshing} onClick={onRefresh} testId="invoices-refresh" />} />

      {!loading && !error && invoices.length > 0 && (
        <Card className="mb-5 flex items-center justify-between rounded-xl border bg-white p-5 shadow-[var(--shadow-sm)]" data-testid="invoices-summary">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-[color:var(--hh-slate)]">Outstanding balance</div>
            <div className="mt-1 font-display text-3xl font-semibold text-[color:var(--hh-ink)]">{formatMoney(outstanding)}</div>
          </div>
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--hh-amber-50)] text-[color:var(--hh-amber-600)]"><ReceiptText className="h-6 w-6" /></span>
        </Card>
      )}

      {loading ? (
        <Card className="rounded-xl border bg-white p-4"><div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div></Card>
      ) : error ? (
        <Alert variant="destructive" data-testid="invoices-error"><AlertCircle className="h-4 w-4" /><AlertTitle>Couldn't load invoices</AlertTitle><AlertDescription className="flex items-center gap-3">{error}<Button size="sm" variant="outline" onClick={refetch}>Retry</Button></AlertDescription></Alert>
      ) : invoices.length === 0 ? (
        <EmptyState icon={ReceiptText} title="No invoices yet" description="Your invoices will appear here once they have been raised." testId="invoices-empty" />
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden overflow-hidden rounded-xl border bg-white shadow-[var(--shadow-sm)] md:block">
            <Table data-testid="invoices-table">
              <TableHeader>
                <TableRow className="bg-[var(--hh-band)] hover:bg-[var(--hh-band)]">
                  <TableHead>Invoice</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} className="hover:bg-[var(--hh-band)]" data-testid={`invoice-row-${inv.id}`}>
                    <TableCell className="font-medium text-[color:var(--hh-ink)]">{inv.xero_invoice_number || "Draft"}<div className="text-xs font-normal text-muted-foreground">{inv.xero_invoice_id ? "Xero" : "Pending sync"}</div></TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-[color:var(--hh-slate)]">{period(inv)}</TableCell>
                    <TableCell className="font-semibold text-[color:var(--hh-ink)]">{formatMoney(inv.total)}</TableCell>
                    <TableCell><StatusBadge status={inv.status} type="invoice" testId={`invoice-status-${inv.id}`} /></TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <PaymentBadge inv={inv} testId={`invoice-payment-${inv.id}`} />
                        <PaymentMethodLine inv={inv} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setSelected(inv)} data-testid={`view-invoice-${inv.id}`}><Eye className="h-4 w-4" /></Button>
                        {inv.xero_invoice_url && <a href={inv.xero_invoice_url} target="_blank" rel="noopener noreferrer" data-testid={`invoice-xero-link-${inv.id}`}><Button size="sm" variant="outline" className="h-8"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Xero</Button></a>}
                        <Button size="sm" variant="outline" className="h-8" disabled={downloading === inv.id} onClick={() => handleDownload(inv)} data-testid={`invoice-download-${inv.id}`}>{downloading === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}<span className="ml-1">PDF</span></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {invoices.map((inv) => (
              <Card key={inv.id} className="rounded-xl border bg-white p-4 shadow-[var(--shadow-sm)]" data-testid={`invoice-card-${inv.id}`}>
                <div className="flex items-start justify-between">
                  <div><div className="font-medium text-[color:var(--hh-ink)]">{inv.xero_invoice_number || "Draft"}</div><div className="text-xs text-muted-foreground">{period(inv)}</div></div>
                  <StatusBadge status={inv.status} type="invoice" />
                </div>
                <div className="mt-2 font-display text-2xl font-semibold text-[color:var(--hh-ink)]">{formatMoney(inv.total)}</div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <PaymentBadge inv={inv} />
                  <PaymentMethodLine inv={inv} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setSelected(inv)}><Eye className="mr-1 h-4 w-4" /> Details</Button>
                  {inv.xero_invoice_url && <a href={inv.xero_invoice_url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="h-8"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Xero</Button></a>}
                  <Button size="sm" variant="outline" className="h-8" onClick={() => handleDownload(inv)}><FileDown className="mr-1 h-3.5 w-3.5" /> PDF</Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Invoice detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="invoice-detail-dialog">
          <DialogHeader><DialogTitle className="font-display">Invoice {selected?.xero_invoice_number || ""}</DialogTitle><DialogDescription className="sr-only">Invoice line items, total and payment status.</DialogDescription></DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{period(selected)}</span>
                <StatusBadge status={selected.status} type="invoice" />
              </div>
              <div className="overflow-hidden rounded-lg border border-[color:var(--hh-line)]">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--hh-band)] text-xs text-[color:var(--hh-slate)]"><tr><th className="px-3 py-2 text-left font-semibold">Description</th><th className="px-3 py-2 text-right font-semibold">Qty</th><th className="px-3 py-2 text-right font-semibold">Amount</th></tr></thead>
                  <tbody>
                    {(selected.line_items || []).map((li, i) => (
                      <tr key={i} className="border-t border-[color:var(--hh-line)]"><td className="px-3 py-2 text-[color:var(--hh-ink)]">{li.description}</td><td className="px-3 py-2 text-right text-[color:var(--hh-slate)]">{li.quantity}</td><td className="px-3 py-2 text-right font-medium text-[color:var(--hh-ink)]">{formatMoney(li.amount)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between border-t border-[color:var(--hh-line)] pt-3">
                <span className="font-semibold text-[color:var(--hh-slate)]">Total</span>
                <span className="font-display text-xl font-semibold text-[color:var(--hh-ink)]">{formatMoney(selected.total)}</span>
              </div>
              <div className="rounded-lg border border-[color:var(--hh-line)] bg-[var(--hh-band)] p-3" data-testid="invoice-payment-detail">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getPayment(selected).MethodIcon && React.createElement(getPayment(selected).MethodIcon, { className: "h-4 w-4 text-[color:var(--hh-amber-600)]" })}
                    <span className="text-xs font-semibold uppercase tracking-wide text-[color:var(--hh-slate)]">Payment{getPayment(selected).method ? ` · ${getPayment(selected).method}` : ""}</span>
                  </div>
                  <PaymentBadge inv={selected} />
                </div>
                <p className="mt-2 text-[13px] text-muted-foreground">
                  {selected.gocardless_payment_id
                    ? ((selected.status || "").toLowerCase() === "paid"
                        ? "This payment has been collected automatically by GoCardless Direct Debit."
                        : (selected.status || "").toLowerCase() === "sent"
                          ? "This invoice will be collected automatically by GoCardless Direct Debit — no action needed."
                          : "This invoice is set up for GoCardless Direct Debit collection.")
                    : ((selected.status || "").toLowerCase() === "paid"
                        ? "This invoice has been paid by bank transfer."
                        : (selected.status || "").toLowerCase() === "sent"
                          ? "Please settle this invoice by bank transfer."
                          : "Payment details will be confirmed once this invoice is issued.")}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button variant="outline" className="rounded-xl" onClick={() => handleDownload(selected)}><FileDown className="mr-1.5 h-4 w-4" /> Download PDF</Button>
                {selected.xero_invoice_url && <a href={selected.xero_invoice_url} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="rounded-xl"><ExternalLink className="mr-1.5 h-4 w-4" /> Open in Xero</Button></a>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
